import React from 'react';
import { BlankModal, HStack, ModalBody, ModalFooter, ModalHeader } from '@cad-arc';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import FireStationInfoForm, {
    FireStationInfoFormData,
    StationContexts,
} from './fire-station-info-form';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useApiClient } from '@m43/core';
import type { SelectOptions } from '../forms/option';
import { StationContextDto, StationsResource } from '@mark43/cad-api';

export type FireStationInfoModalComponentType = typeof FireStationInfoModal;

const FireStationInfoModal = () => {
    const [stationOptions, setStationOptions] = React.useState<SelectOptions>([]);
    const [stationContext, setStationContext] = React.useState<StationContexts>();

    const apiClient = useApiClient();

    const modalControls = useFullscreenModalControls();

    const closeModal = React.useCallback(() => {
        modalControls.close();
    }, [modalControls]);

    const stationsQuery = useQuery(['fire-station-info'], () => {
        return apiClient
            .request<StationsResource.BootstrapStationContext>({
                method: 'POST',
                url: 'stations/context/bootstrap',
                data: {},
            })
            .then((resp) => {
                const sortedStations = resp?.data?.data?.stations.sort((a, b) =>
                    a.label.localeCompare(b.label)
                );

                setStationOptions(sortedStations ?? []);
                setStationContext(resp?.data?.data?.stationContextByStationId);
                return resp?.data.data;
            });
    });

    const form = useForm<FireStationInfoFormData>({
        defaultValues: {
            availablePersonnelCount: '-',
            assignedPersonnelCount: '-',
        },
        mode: 'onBlur',
    });

    // When station changes, populate the other fields with the associated station data
    React.useEffect(() => {
        const subscription = form.watch((value, { name }) => {
            const selected = value.station;
            const context = selected?.value != null ? stationContext?.[selected.value] : undefined;

            const originalTotalPersonnelCount = context?.totalPersonnelCount ?? 0;
            const originalAvailablePersonnelCount = context?.availablePersonnelCount ?? 0;

            const totalPersonnelCount =
                parseInt(form.getValues('totalPersonnelCount') ?? '0', 10) || 0;

            if (name === 'station') {
                form.clearErrors();

                form.setValue('totalPersonnelCount', originalTotalPersonnelCount.toString());
                form.setValue(
                    'availablePersonnelCount',
                    originalAvailablePersonnelCount.toString()
                );
                form.setValue(
                    'assignedPersonnelCount',
                    (originalTotalPersonnelCount - originalAvailablePersonnelCount).toString()
                );

                form.setValue('resetAvailablePersonnel', false);
                form.setValue(
                    'availablePersonnelCountOverride',
                    originalAvailablePersonnelCount.toString()
                );
                return;
            } else if (name === 'totalPersonnelCount') {
                // Re-validate the dependent field since max changed
                form.trigger('availablePersonnelCountOverride');
            } else if (name === 'resetAvailablePersonnel') {
                form.setValue(
                    'availablePersonnelCountOverride',
                    Math.min(totalPersonnelCount, originalAvailablePersonnelCount).toString(),
                    {
                        shouldValidate: true,
                    }
                );
            }
        });
        return () => subscription.unsubscribe();
    }, [form, stationContext]);

    const mutation = useMutation(
        (values: FireStationInfoFormData) => {
            const stationId = values.station!.value;

            const totalPersonnelCount = parseInt(values.totalPersonnelCount.trim(), 10);
            const availablePersonnelCountOverride =
                values.resetAvailablePersonnel &&
                values.availablePersonnelCountOverride &&
                values.availablePersonnelCountOverride !== ''
                    ? parseInt(values.availablePersonnelCountOverride.trim(), 10)
                    : undefined;

            const stationContextPayload: StationContextDto = {
                stationId,
                totalPersonnelCount,
                availablePersonnelCount: availablePersonnelCountOverride,
            };

            return apiClient.request<StationsResource.SetStationPersonnelCount>({
                method: 'POST',
                url: 'stations/context',
                data: {
                    stationContexts: [stationContextPayload],
                },
            });
        },
        {
            onSuccess: () => closeModal(),
        }
    );

    const onSubmit = React.useCallback(
        (values: FireStationInfoFormData) => {
            mutation.mutate(values);
        },
        [mutation]
    );

    return (
        <IntegratedFormProvider
            query={stationsQuery}
            form={form}
            mutation={mutation}
            onSubmit={onSubmit}
        >
            <BlankModal size="sm" isOpen={true} onClose={closeModal}>
                <ModalHeader>Fire Station Information</ModalHeader>
                <ModalBody>
                    <FireStationInfoForm
                        stationOptions={stationOptions}
                        stationContext={stationContext}
                    />
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton onClick={closeModal} />
                        <FormSaveButton />
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};

export default FireStationInfoModal;
